import io from 'socket.io-client';

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
  }

  /**
   * Connect to Socket.io server
   */
  connect(token) {
    if (this.socket?.connected) {
      console.log('Socket already connected');
      return this.socket;
    }

    const serverUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';

    this.socket = io(serverUrl, {
      auth: {
        token: token
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5
    });

    // Connection events
    this.socket.on('connect', () => {
      console.log('✅ Connected to Socket.io server');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ Disconnected from Socket.io:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
    });

    return this.socket;
  }

  /**
   * Disconnect from server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
    }
  }

  /**
   * Join a channel
   */
  joinChannel(channelId) {
    if (this.socket) {
      this.socket.emit('channel:join', channelId);
    }
  }

  /**
   * Leave a channel
   */
  leaveChannel(channelId) {
    if (this.socket) {
      this.socket.emit('channel:leave', channelId);
    }
  }

  /**
   * Send a message
   */
  sendMessage({ channelId, content, type = 'text', metadata = {}, tempId }) {
    if (this.socket) {
      this.socket.emit('message:send', {
        channelId,
        content,
        type,
        metadata,
        tempId
      });
    }
  }

  /**
   * Start typing indicator
   */
  startTyping(channelId) {
    if (this.socket) {
      this.socket.emit('typing:start', { channelId });
    }
  }

  /**
   * Stop typing indicator
   */
  stopTyping(channelId) {
    if (this.socket) {
      this.socket.emit('typing:stop', { channelId });
    }
  }

  /**
   * Mark message as read
   */
  markAsRead(messageId, channelId) {
    if (this.socket) {
      this.socket.emit('message:read', { messageId, channelId });
    }
  }

  /**
   * Update user status
   */
  updateStatus(status) {
    if (this.socket) {
      this.socket.emit('status:update', status);
    }
  }

  /**
   * Listen to an event
   */
  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
      
      // Store listener for cleanup
      if (!this.listeners.has(event)) {
        this.listeners.set(event, []);
      }
      this.listeners.get(event).push(callback);
    }
  }

  /**
   * Remove event listener
   */
  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
      
      // Remove from stored listeners
      if (this.listeners.has(event)) {
        const callbacks = this.listeners.get(event);
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    }
  }

  /**
   * Remove all listeners for an event
   */
  removeAllListeners(event) {
    if (this.socket) {
      this.socket.removeAllListeners(event);
      this.listeners.delete(event);
    }
  }

  /**
   * Check if connected
   */
  isConnected() {
    return this.socket?.connected || false;
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
